import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Plus, ChevronLeft, Sparkles } from 'lucide-react';
import { apiFetch } from '../utils/api';
import WorkoutDayEditor from '../components/WorkoutDayEditor';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';

const WorkoutPreviewPage = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const initial = state?.workout;

  const [name, setName] = useState(initial?.name || '');
  const [description, setDescription] = useState(initial?.description || '');
  const [days, setDays] = useState(
    (initial?.days || []).map((d, idx) => ({
      ...d,
      day_number: d.day_number ?? idx + 1,
    }))
  );
  const [saving, setSaving] = useState(false);
  const [nameError, setNameError] = useState(false);
  const [error, setError] = useState('');

  if (!initial) {
    return (
      <div className="page-container">
        <p style={{ color: 'var(--muted-foreground)' }}>{t('workout_preview.error_loading')}</p>
      </div>
    );
  }

  const addDay = () =>
    setDays(prev => [
      ...prev,
      {
        day_number: prev.length + 1,
        name: i18n.t('workout.day_default_name', { number: prev.length + 1 }),
        exercises: [],
      },
    ]);

  const removeDay = (dayIndex) =>
    setDays(prev => prev.filter((_, i) => i !== dayIndex));

  const updateDayName = (dayIndex, value) =>
    setDays(prev => prev.map((d, i) => (i === dayIndex ? { ...d, name: value } : d)));

  const addExercise = (dayIndex) =>
    setDays(prev =>
      prev.map((d, i) =>
        i === dayIndex
          ? { ...d, exercises: [...d.exercises, { name: '', sets: '', notes: '' }] }
          : d
      )
    );

  const updateExercise = (dayIndex, exIndex, field, value) =>
    setDays(prev =>
      prev.map((d, i) =>
        i === dayIndex
          ? {
              ...d,
              exercises: d.exercises.map((ex, j) =>
                j === exIndex ? { ...ex, [field]: value } : ex
              ),
            }
          : d
      )
    );

  const removeExercise = (dayIndex, exIndex) =>
    setDays(prev =>
      prev.map((d, i) =>
        i === dayIndex
          ? { ...d, exercises: d.exercises.filter((_, j) => j !== exIndex) }
          : d
      )
    );

  const handleSave = async () => {
    if (!name.trim()) { setNameError(true); return; }
    setNameError(false);
    setError('');
    setSaving(true);
    try {
      const res = await apiFetch('/api/workouts', {
        method: 'POST',
        body: JSON.stringify({ name, description, days }),
      });
      if (res.ok) {
        navigate('/dashboard');
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data.detail || t('workout.error_saving'));
      }
    } catch {
      setError(t('workout.error_saving'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page-container animate-fade-in">
      <div className="workout-page-header">
        <button className="workout-page-back" onClick={() => navigate(-1)}>
          <ChevronLeft size={14} />
          {t('common.back')}
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
          <h1 className="header-title" style={{ fontSize: '22px' }}>{t('workout_preview.title')}</h1>
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: '4px',
            padding: '2px 8px', borderRadius: '20px',
            background: 'rgba(var(--primary-rgb),0.15)', border: '1px solid rgba(var(--primary-rgb),0.3)',
            fontSize: '11px', fontWeight: '700', color: 'var(--primary)',
            textTransform: 'uppercase', letterSpacing: '0.06em',
            fontFamily: "'Barlow Condensed', sans-serif",
          }}>
            <Sparkles size={10} /> IA
          </span>
        </div>
        <p style={{ fontSize: '13px', color: 'var(--muted-foreground)' }}>
          {t('workout_preview.subtitle')}
        </p>
      </div>

      <div className="workout-info-card">
        <div className="input-group">
          <label className="input-label">{t('workout.routine_name_label')}</label>
          <input
            type="text"
            className="input"
            placeholder={t('workout.routine_name_placeholder')}
            value={name}
            onChange={(e) => { setName(e.target.value); if (nameError) setNameError(false); }}
            style={nameError ? { borderColor: 'var(--destructive)' } : {}}
          />
          {nameError && (
            <p style={{ color: 'var(--destructive)', fontSize: '12px', marginTop: '6px' }}>
              {t('workout.name_required')}
            </p>
          )}
        </div>
        <div className="input-group" style={{ marginBottom: 0 }}>
          <label className="input-label">
            {t('workout.description_label')}{' '}
            <span style={{ color: 'var(--muted-foreground)', fontWeight: 400, textTransform: 'none' }}>
              {t('common.optional')}
            </span>
          </label>
          <input
            type="text"
            className="input"
            placeholder={t('workout.description_placeholder')}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>
      </div>

      <p className="days-section-title">
        {t('workout.training_days')}
        <span style={{
          marginLeft: '8px', fontSize: '11px', fontWeight: '600',
          color: 'var(--muted-foreground)', textTransform: 'none', letterSpacing: 0,
        }}>
          ({days.length} {days.length === 1 ? t('workout_preview.day_singular') : t('workout_preview.day_plural')})
        </span>
      </p>

      {days.map((day, dayIndex) => (
        <WorkoutDayEditor
          key={dayIndex}
          day={day}
          dayIndex={dayIndex}
          totalDays={days.length}
          onUpdateName={updateDayName}
          onAddExercise={addExercise}
          onUpdateExercise={updateExercise}
          onRemoveExercise={removeExercise}
          onRemoveDay={removeDay}
        />
      ))}

      <button className="btn-add-day" onClick={addDay}>
        <Plus size={16} />
        {t('workout.add_day')}
      </button>

      {error && (
        <p style={{
          color: 'var(--destructive)', fontSize: '13px',
          textAlign: 'center', marginBottom: '12px',
        }}>
          {error}
        </p>
      )}

      <div className="save-section" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        <button
          className="btn btn-primary w-full"
          onClick={handleSave}
          disabled={saving}
        >
          {saving
            ? <div className="loading-spinner" style={{ width: '20px', height: '20px' }} />
            : t('workout_preview.load_routine')
          }
        </button>
        <button
          className="btn btn-secondary w-full"
          onClick={() => navigate(-1)}
          disabled={saving}
        >
          {t('workout_preview.cancel')}
        </button>
      </div>
    </div>
  );
};

export default WorkoutPreviewPage;
